import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { 
  ArrowLeft, User, Building, Calendar, 
  FileText, DollarSign, Clock, CheckCircle, AlertTriangle,
  Edit, Eye, Plus, TrendingUp, Activity
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { usePageTitle } from '../hooks/usePageTitle';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';
import { useToast, toast } from '../components/UI/Toast';
import { ApiService, ClientDetailDto, ClientEngagementDto, ClientStatus, EngagementStatus as EngagementStatusType } from '../services/api';
import { authService } from '../services/authService';
import { FullPageIframeModal, buildClientProfileUrl } from '../components/UI/FullPageIframeModal';

type EngagementFilter = 'all' | EngagementStatusType;

// Animation keyframes
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
  animation: ${fadeInUp} 0.6s ease-out;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const HeaderSection = styled.div`
  margin-bottom: 2.5rem;
`;

const BackButton = styled.button.withConfig({
  shouldForwardProp: (prop) => !['theme'].includes(prop)
})<{ theme: any }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  border: 1px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 1.5rem;
  
  &:hover {
    background: ${props => props.theme.colors.background};
    color: ${props => props.theme.colors.text};
    border-color: ${props => props.theme.colors.primary};
  }
`;

const ClientHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
  }
`;

const ClientInfo = styled.div`
  flex: 1;
`;

const ClientName = styled.h1<{ theme: any }>`
  color: ${props => props.theme.colors.text};
  font-size: 1.55rem;
  font-weight: 800;
  margin: 0 0 0.5rem 0;
  letter-spacing: -0.02em;
`;

const ClientCompany = styled.h2<{ theme: any }>`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StatusBadge = styled.div.withConfig({
  shouldForwardProp: (prop) => !['theme', 'status'].includes(prop)
})<{ theme: any; status: ClientStatus }>`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  ${props => {
    switch (props.status) {
      case 'ACTIVE':
        return `
          background: #16a34a20;
          color: #16a34a;
        `;
      case 'INACTIVE':
        return `
          background: #dc262620;
          color: #dc2626;
        `;
      case 'ARCHIVED':
        return `
          background: #64748b20;
          color: #64748b;
        `;
      default:
        return `
          background: ${props.theme.colors.border};
          color: ${props.theme.colors.textSecondary};
        `;
    }
  }}
`;

const ClientActions = styled.div`
  display: flex;
  gap: 0.75rem;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
  }
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2.5rem;
`;

const StatCard = styled(Card).withConfig({
  shouldForwardProp: (prop) => !['theme'].includes(prop)
})<{ theme: any }>`
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  animation: ${slideInLeft} 0.6s ease-out;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }
`;

const StatIcon = styled.div<{ theme: any; color: string }>`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${props => props.color}15;
  color: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatValue = styled.div<{ theme: any }>`
  font-size: 1.2rem;
  font-weight: 800;
  color: ${props => props.theme.colors.text};
  line-height: 1;
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div<{ theme: any }>`
  font-size: 0.875rem;
  color: ${props => props.theme.colors.textSecondary};
  font-weight: 500;
`;

const TabsBar = styled.div<{ theme: any }>`
  display: inline-flex;
  gap: 0.5rem;
  padding: 0.4rem;
  background: ${p => p.theme.colors.surface};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 14px;
  margin: 0 0 1.5rem 0;
`;

const TabButton = styled.button.withConfig({
  shouldForwardProp: (prop) => !['theme', 'active'].includes(prop),
})<{ theme: any; active: boolean }>`
  border: 1px solid ${p => (p.active ? p.theme.colors.primary : 'transparent')};
  background: ${p => (p.active ? p.theme.colors.primary : 'transparent')};
  color: ${p => (p.active ? 'white' : p.theme.colors.text)};
  border-radius: 12px;
  padding: 0.6rem 0.9rem;
  cursor: pointer;
  font-weight: 900;
  font-size: 0.9rem;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${p => p.theme.colors.primary};
    background: ${p => (p.active ? p.theme.colors.primary : `${p.theme.colors.primary}12`)};
    color: ${p => (p.active ? 'white' : p.theme.colors.primary)};
  }
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const SectionTitle = styled.h3<{ theme: any }>`
  color: ${props => props.theme.colors.text};
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const DetailLabel = styled.span<{ theme: any }>`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DetailValue = styled.span<{ theme: any }>`
  color: ${props => props.theme.colors.text};
  font-size: 0.95rem;
  font-weight: 500;
  word-break: break-word;
`;

const EngagementsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const EngagementCard = styled(Card).withConfig({
  shouldForwardProp: (prop) => !['theme'].includes(prop)
})<{ theme: any }>`
  padding: 1.5rem;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }
`;

const EngagementHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const EngagementInfo = styled.div`
  flex: 1;
`;

const EngagementTitle = styled.h4<{ theme: any }>`
  color: ${props => props.theme.colors.text};
  font-size: 1.125rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
`;

const EngagementMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 0.75rem;
`;

const MetaItem = styled.div<{ theme: any }>`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.875rem;
`;

const EngagementStatusBadge = styled.div.withConfig({
  shouldForwardProp: (prop) => !['theme', 'status'].includes(prop)
})<{ theme: any; status: EngagementStatusType }>`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  ${props => {
    switch (props.status) {
      case 'CLOSED':
        return `
          background: #16a34a20;
          color: #16a34a;
        `;
      case 'OPEN':
        return `
          background: #f59e0b20;
          color: #f59e0b;
        `;
      default:
        return `
          background: ${props.theme.colors.border};
          color: ${props.theme.colors.textSecondary};
        `;
    }
  }}
`;

const EngagementActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const FilterSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }
`;

const FilterButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const FilterButton = styled.button.withConfig({
  shouldForwardProp: (prop) => !['theme', 'active'].includes(prop)
})<{ theme: any; active: boolean }>`
  padding: 0.5rem 1rem;
  border-radius: 8px;
  border: 2px solid ${props => props.active ? props.theme.colors.primary : props.theme.colors.border};
  background: ${props => props.active ? props.theme.colors.primary : props.theme.colors.surface};
  color: ${props => props.active ? 'white' : props.theme.colors.text};
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background: ${props => props.active ? props.theme.colors.primary : props.theme.colors.primary}10;
    color: ${props => props.active ? 'white' : props.theme.colors.primary};
  }
`;

// Accounting Year Modal
const AccountingModalOverlay = styled.div.withConfig({
  shouldForwardProp: (prop) => !['theme', 'isOpen'].includes(prop),
})<{ theme: any; isOpen: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  z-index: 1200;
  opacity: ${p => (p.isOpen ? 1 : 0)};
  visibility: ${p => (p.isOpen ? 'visible' : 'hidden')};
  transition: opacity 0.2s ease, visibility 0.2s ease;
`;

const AccountingModalCard = styled.div<{ theme: any }>`
  width: 520px;
  max-width: 100%;
  background: ${p => p.theme.colors.surface};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 16px;
  box-shadow: 0 24px 70px rgba(0,0,0,0.22);
  overflow: hidden;
`;

const AccountingModalHeader = styled.div<{ theme: any }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.25rem 1rem;
  border-bottom: 1px solid ${p => p.theme.colors.border};
`;

const AccountingModalTitle = styled.div<{ theme: any }>`
  font-weight: 900;
  color: ${p => p.theme.colors.text};
`;

const AccountingModalClose = styled.button<{ theme: any }>`
  border: 1px solid ${p => p.theme.colors.border};
  background: transparent;
  color: ${p => p.theme.colors.text};
  border-radius: 10px;
  padding: 0.35rem 0.6rem;
  cursor: pointer;
  font-weight: 900;
`;

const AccountingModalBody = styled.div`
  padding: 1rem 1.25rem 1.25rem;
`;

const AccountingFieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const AccountingLabel = styled.div<{ theme: any }>`
  font-size: 0.8rem;
  font-weight: 800;
  color: ${p => p.theme.colors.textSecondary};
  margin-bottom: 0.35rem;
`;

const AccountingSelect = styled.select<{ theme: any }>`
  width: 100%;
  padding: 0.7rem 0.8rem;
  border-radius: 12px;
  border: 1px solid ${p => p.theme.colors.border};
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.text};
  font-weight: 700;
`;

const AccountingModalFooter = styled.div<{ theme: any }>`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border-top: 1px solid ${p => p.theme.colors.border};
`;

const ClientProfilePage: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<'info' | 'engagements'>('info');
  const [engagementFilter, setEngagementFilter] = useState<EngagementFilter>('all');
  const [client, setClient] = useState<ClientDetailDto | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acctOpen, setAcctOpen] = useState(false);
  const [acctSaving, setAcctSaving] = useState(false);
  const [filingsOpen, setFilingsOpen] = useState(false);
  const [filingsUrl, setFilingsUrl] = useState<string>('');
  const [filingsLoading, setFilingsLoading] = useState(false);

  const months = useMemo(() => ([
    { label: 'Jan', value: 0 },
    { label: 'Feb', value: 1 },
    { label: 'Mar', value: 2 },
    { label: 'Apr', value: 3 },
    { label: 'May', value: 4 },
    { label: 'Jun', value: 5 },
    { label: 'Jul', value: 6 },
    { label: 'Aug', value: 7 },
    { label: 'Sep', value: 8 },
    { label: 'Oct', value: 9 },
    { label: 'Nov', value: 10 },
    { label: 'Dec', value: 11 },
  ]), []);

  const [startMonth, setStartMonth] = useState(0);
  const [startDay, setStartDay] = useState(1);
  const [endMonth, setEndMonth] = useState(11);
  const [endDay, setEndDay] = useState(31);

  useEffect(() => {
    const run = async () => {
      if (!clientId) return;
      const token = authService.getAccessToken();
      if (!token) return;
      setIsLoading(true);
      setError(null);
      try {
        const resp = await ApiService.getClient(token, clientId);
        setClient(resp.data);
      } catch (e: any) {
        setError(e?.message || 'Failed to load client');
      } finally {
        setIsLoading(false);
      }
    };
    void run();
  }, [clientId]);

  const engagements: ClientEngagementDto[] = useMemo(() => client?.engagements || [], [client?.engagements]);

  const filteredEngagements = useMemo(() => {
    if (engagementFilter === 'all') return engagements;
    return engagements.filter(eng => eng.status === engagementFilter);
  }, [engagements, engagementFilter]);

  const displayName = client?.companyName || client?.email || client?.tin || 'Client';
  usePageTitle(`${displayName} - Profile`);

  useEffect(() => {
    if (!client?.accountingYear) return;
    const m = client.accountingYear.match(/^([A-Za-z]{3})\s+(\d{1,2})\s*-\s*([A-Za-z]{3})\s+(\d{1,2})$/);
    if (!m) return;
    const sm = months.find(x => x.label.toLowerCase() === m[1].toLowerCase())?.value;
    const em = months.find(x => x.label.toLowerCase() === m[3].toLowerCase())?.value;
    if (sm === undefined || em === undefined) return;
    setStartMonth(sm);
    setStartDay(Math.min(31, Math.max(1, Number(m[2]))));
    setEndMonth(em);
    setEndDay(Math.min(31, Math.max(1, Number(m[4]))));
  }, [client?.accountingYear, months]);

  const accountingYearString = () => {
    const sM = months.find(m => m.value === startMonth)?.label || 'Jan';
    const eM = months.find(m => m.value === endMonth)?.label || 'Dec';
    return `${sM} ${startDay} - ${eM} ${endDay}`;
  };

  const saveAccountingYear = async () => {
    if (!clientId || !client?.consultantId) return;
    const token = authService.getAccessToken();
    if (!token) return;
    setAcctSaving(true);
    try {
      await ApiService.updateClientAccountingYear(token, clientId, client.consultantId, accountingYearString());
      setClient(prev => prev ? ({ ...prev, accountingYear: accountingYearString() }) : prev);
      showToast(toast.success('Accounting Year Updated', 'Accounting year has been saved.'));
      setAcctOpen(false);
    } catch (e: any) {
      showToast(toast.error('Update Failed', e?.message || 'Could not update accounting year'));
    } finally {
      setAcctSaving(false);
    }
  };

  const formatDate = (iso?: string | null) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(d);
  };

  const getStatusIcon = (status: EngagementStatusType) => {
    switch (status) {
      case 'CLOSED': return <CheckCircle size={12} />;
      case 'OPEN': return <Clock size={12} />;
      default: return <Clock size={12} />;
    }
  };

  const openManageFilings = async () => {
    const base = process.env.REACT_APP_CLIENT_PROFILE_ENDPOINT;
    if (!base) {
      showToast(toast.error('Missing configuration', 'Client profile endpoint is not configured.'));
      return;
    }

    if (!clientId) return;

    const token = authService.getAccessToken();
    if (!token) {
      showToast(toast.error('Not authenticated', 'Please log in again and try.'));
      return;
    }

    setFilingsLoading(true);
    try {
      const clientToken = await ApiService.getClientToken(token, clientId);
      const url = buildClientProfileUrl({ baseUrl: base, clientToken });
      setFilingsUrl(url);
      setFilingsOpen(true);
    } catch (e: any) {
      showToast(toast.error('Could not open filings', e?.message || 'Please try again.'));
    } finally {
      setFilingsLoading(false);
    }
  };

  return (
    <Container>
      <HeaderSection>
        <BackButton theme={theme} onClick={() => navigate('/clients')}>
          <ArrowLeft size={16} />
          Back to Clients
        </BackButton>

        <ClientHeader>
          <ClientInfo>
            <ClientName theme={theme}>{displayName}</ClientName>
            <ClientCompany theme={theme}>
              <Building size={20} />
              {client?.industry || '—'}
            </ClientCompany>
            {client && (
              <StatusBadge theme={theme} status={client.status}>
                {client.status === 'ACTIVE' && <CheckCircle size={12} />}
                {client.status === 'INACTIVE' && <AlertTriangle size={12} />}
                {client.status === 'ARCHIVED' && <Clock size={12} />}
                {client.status}
              </StatusBadge>
            )}
          </ClientInfo>
          <ClientActions>
            {client?.status === 'ACTIVE' && (
              <Button
                onClick={openManageFilings}
                disabled={filingsLoading}
              >
                {filingsLoading ? 'Opening…' : 'Manage Filings'}
              </Button>
            )}
            <Button variant="outline" onClick={() => setAcctOpen(true)} disabled={!client}>
              <Edit size={16} />
            </Button>
          </ClientActions>
        </ClientHeader>
      </HeaderSection>

      {error && (
        <Card>
          <div style={{ padding: '1rem 1.25rem' }}>
            <div style={{ fontWeight: 900, marginBottom: '0.25rem', color: theme.colors.text }}>Could not load client</div>
            <div style={{ fontWeight: 600, color: theme.colors.textSecondary }}>{error}</div>
          </div>
        </Card>
      )}

      {isLoading && (
        <Card>
          <div style={{ padding: '1rem 1.25rem', color: theme.colors.textSecondary, fontWeight: 700 }}>
            Loading client...
          </div>
        </Card>
      )}

      <StatsRow>
        <StatCard theme={theme}>
          <StatIcon theme={theme} color={theme.colors.primary}>
            <FileText size={20} />
          </StatIcon>
          <StatContent>
            <StatValue theme={theme}>{engagements.length}</StatValue>
            <StatLabel theme={theme}>Total Engagements</StatLabel>
          </StatContent>
        </StatCard>
        <StatCard theme={theme}>
          <StatIcon theme={theme} color="#16a34a">
            <DollarSign size={20} />
          </StatIcon>
          <StatContent>
            <StatValue theme={theme}>{client?.tin || '—'}</StatValue>
            <StatLabel theme={theme}>Tax ID</StatLabel>
          </StatContent>
        </StatCard>
        <StatCard theme={theme}>
          <StatIcon theme={theme} color="#0ea5e9">
            <Calendar size={20} />
          </StatIcon>
          <StatContent>
            <StatValue theme={theme}>{formatDate(client?.createdAt ?? null)}</StatValue>
            <StatLabel theme={theme}>Created</StatLabel>
          </StatContent>
        </StatCard>
        <StatCard theme={theme}>
          <StatIcon theme={theme} color="#f59e0b">
            <TrendingUp size={20} />
          </StatIcon>
          <StatContent>
            <StatValue theme={theme}>{client?.status || '—'}</StatValue>
            <StatLabel theme={theme}>Industry</StatLabel>
          </StatContent>
        </StatCard>
      </StatsRow>

      <TabsBar theme={theme}>
        <TabButton
          theme={theme}
          active={activeTab === 'info'}
          onClick={() => setActiveTab('info')}
        >
          Client Information
        </TabButton>
        <TabButton
          theme={theme}
          active={activeTab === 'engagements'}
          onClick={() => setActiveTab('engagements')}
        >
          Engagement History
        </TabButton>
      </TabsBar>

      <MainGrid>
        {activeTab === 'info' && (
          <Section>
            <Card>
              <SectionTitle theme={theme}>
                <User size={20} />
                Client Information
              </SectionTitle>
              <DetailGrid>
                <DetailItem>
                  <DetailLabel theme={theme}>Email Address</DetailLabel>
                  <DetailValue theme={theme}>{client?.email || '—'}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel theme={theme}>Phone Number</DetailLabel>
                  <DetailValue theme={theme}>{client?.phone || '—'}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel theme={theme}>Tax ID</DetailLabel>
                  <DetailValue theme={theme}>{client?.tin || '—'}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel theme={theme}>CAC Number</DetailLabel>
                  <DetailValue theme={theme}>{client?.cacNumber || '—'}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel theme={theme}>Address</DetailLabel>
                  <DetailValue theme={theme}>{client?.address || '—'}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel theme={theme}>Registration Date</DetailLabel>
                  <DetailValue theme={theme}>{client?.registrationDate || '—'}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel theme={theme}>Accounting Year End</DetailLabel>
                  <DetailValue theme={theme}>{client?.accountingYear || '—'}</DetailValue>
                </DetailItem>
              </DetailGrid>
            </Card>
          </Section>
        )}

        {activeTab === 'engagements' && (
          <Section>
            <Card>
              <FilterSection>
                <SectionTitle theme={theme}>
                  <Activity size={20} />
                  Engagement History
                </SectionTitle>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <FilterButtons>
                    <FilterButton
                      theme={theme}
                      active={engagementFilter === 'all'}
                      onClick={() => setEngagementFilter('all')}
                    >
                      All
                    </FilterButton>
                    <FilterButton
                      theme={theme}
                      active={engagementFilter === 'CLOSED'}
                      onClick={() => setEngagementFilter('CLOSED')}
                    >
                      Closed
                    </FilterButton>
                    <FilterButton
                      theme={theme}
                      active={engagementFilter === 'OPEN'}
                      onClick={() => setEngagementFilter('OPEN')}
                    >
                      Open
                    </FilterButton>
                  </FilterButtons>

                  <Button
                    size="small"
                    onClick={() => showToast(toast.info('New Engagement', 'Engagement creation will be available here shortly.'))}
                    disabled={client?.status !== 'ACTIVE'}
                  >
                    <Plus size={16} style={{ marginRight: '0.5rem' }} />
                    New Engagement
                  </Button>
                </div>
              </FilterSection>

              <EngagementsList>
                {filteredEngagements.map((engagement) => (
                  <EngagementCard key={engagement.id} theme={theme}>
                    <EngagementHeader>
                      <EngagementInfo>
                        <EngagementTitle theme={theme}>{engagement.title}</EngagementTitle>
                        <EngagementMeta>
                          <MetaItem theme={theme}>
                            <FileText size={14} />
                            {engagement.type}
                          </MetaItem>
                          <MetaItem theme={theme}>
                            <DollarSign size={14} />
                            {engagement.priority}
                          </MetaItem>
                          <MetaItem theme={theme}>
                            <Calendar size={14} />
                            {formatDate(engagement.startDate)}
                            {engagement.dueDate && ` - ${formatDate(engagement.dueDate)}`}
                          </MetaItem>
                        </EngagementMeta>
                      </EngagementInfo>
                      <EngagementStatusBadge theme={theme} status={engagement.status}>
                        {getStatusIcon(engagement.status)}
                        {engagement.status}
                      </EngagementStatusBadge>
                    </EngagementHeader>
                    
                    <EngagementActions>
                      <Button variant="outline" size="small">
                        <Eye size={14} style={{ marginRight: '0.5rem' }} />
                        View Details
                      </Button>
                    </EngagementActions>
                  </EngagementCard>
                ))}
              </EngagementsList>

              {filteredEngagements.length === 0 && (
                <div style={{ textAlign: 'center', padding: '3rem 2rem' }}>
                  <Activity size={48} style={{ color: theme.colors.textSecondary, marginBottom: '1rem' }} />
                  <h3 style={{ color: theme.colors.text, marginBottom: '0.5rem' }}>No Engagements Found</h3>
                  <p style={{ color: theme.colors.textSecondary, margin: 0 }}>
                    No engagements match the selected filter.
                  </p>
                </div>
              )}
            </Card>
          </Section>
        )}
      </MainGrid>

      <AccountingModalOverlay
        theme={theme}
        isOpen={acctOpen}
        onClick={() => setAcctOpen(false)}
        role="dialog"
        aria-modal="true"
      >
        <AccountingModalCard theme={theme} onClick={(e) => e.stopPropagation()}>
          <AccountingModalHeader theme={theme}>
            <AccountingModalTitle theme={theme}>Update Accounting Year</AccountingModalTitle>
            <AccountingModalClose theme={theme} onClick={() => setAcctOpen(false)} aria-label="Close">
              ✕
            </AccountingModalClose>
          </AccountingModalHeader>
          <AccountingModalBody>
            <div style={{ color: theme.colors.textSecondary, fontWeight: 700, marginBottom: '0.75rem' }}>
              Saved as:{' '}
              <span style={{ color: theme.colors.text, fontWeight: 900 }}>
                {accountingYearString()}
              </span>
            </div>
            <AccountingFieldRow>
              <div>
                <AccountingLabel theme={theme}>Start Month</AccountingLabel>
                <AccountingSelect theme={theme} value={startMonth} onChange={(e) => setStartMonth(Number(e.target.value))}>
                  {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </AccountingSelect>
              </div>
              <div>
                <AccountingLabel theme={theme}>Start Day</AccountingLabel>
                <AccountingSelect theme={theme} value={startDay} onChange={(e) => setStartDay(Number(e.target.value))}>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}</option>)}
                </AccountingSelect>
              </div>
              <div>
                <AccountingLabel theme={theme}>End Month</AccountingLabel>
                <AccountingSelect theme={theme} value={endMonth} onChange={(e) => setEndMonth(Number(e.target.value))}>
                  {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </AccountingSelect>
              </div>
              <div>
                <AccountingLabel theme={theme}>End Day</AccountingLabel>
                <AccountingSelect theme={theme} value={endDay} onChange={(e) => setEndDay(Number(e.target.value))}>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}</option>)}
                </AccountingSelect>
              </div>
            </AccountingFieldRow>
          </AccountingModalBody>
          <AccountingModalFooter theme={theme}>
            <Button variant="outline" onClick={() => setAcctOpen(false)} disabled={acctSaving}>
              Cancel
            </Button>
            <Button onClick={saveAccountingYear} disabled={acctSaving}>
              {acctSaving ? 'Saving...' : 'Save'}
            </Button>
          </AccountingModalFooter>
        </AccountingModalCard>
      </AccountingModalOverlay>

      <FullPageIframeModal
        isOpen={filingsOpen}
        title="Manage Filings"
        contextTitle={displayName}
        url={filingsUrl}
        theme={theme}
        onClose={() => {
          setFilingsOpen(false);
          setFilingsUrl('');
        }}
      />
    </Container>
  );
};

export default ClientProfilePage;
